import type {FC} from 'react';
import {ButtonBase} from '@mui/material';
import {Box, Icon, TextField, Typography} from '@wellstone-solutions/web';
import type {FeedbackFormApi} from '../../useFeedbackFormState';
import type {FeedbackQuestion, FormOption} from '../../types';

export const EXCLUSIVE_VALUE = 'nothing';

type OptionButtonProps = {
  option: FormOption;
  on: boolean;
  tile: boolean;
  onPick: () => void;
};

const OptionButton: FC<OptionButtonProps> = ({option, on, tile, onPick}) => (
  <ButtonBase
    aria-pressed={on}
    onClick={onPick}
    sx={{
      display: 'flex',
      gap: tile ? 0.75 : 1,
      flexDirection: tile ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: 'center',
      ...(tile
        ? {width: 148, height: 84, borderRadius: 2}
        : {height: 40, px: 2, borderRadius: 999}),
      fontSize: 14,
      whiteSpace: 'nowrap',
      bgcolor: on ? 'rgba(81, 45, 109, 0.08)' : 'background.paper',
      border: on ? '1.5px solid' : '1px solid',
      borderColor: on ? 'primary.main' : 'divider',
      color: on ? 'primary.main' : 'text.primary',
    }}>
    {option.icon && <Icon name={option.icon} size={tile ? 24 : 19} />}
    {option.label}
  </ButtonBase>
);

type QuestionFieldProps = {
  question: FeedbackQuestion;
  form: FeedbackFormApi;
  divider: boolean;
};

export const QuestionField: FC<QuestionFieldProps> = ({
  question,
  form,
  divider,
}) => {
  const answer = form.state[question.id];
  const error = form.submitted ? form.errors[question.id] : undefined;

  if (question.type === 'text-field' || question.type === 'text-area') {
    return (
      <Box
        sx={{
          py: 2.25,
          borderTop: divider ? '1px solid' : 0,
          borderColor: 'divider',
        }}>
        <Typography sx={{fontSize: 14.5, fontWeight: 600}}>
          {question.label}
          {!question.required && (
            <Box
              component="span"
              sx={{fontWeight: 400, color: 'text.secondary'}}>
              {' '}
              (optional)
            </Box>
          )}
        </Typography>
        <TextField
          fullWidth
          multiline={question.type === 'text-area'}
          minRows={question.type === 'text-area' ? 3 : undefined}
          placeholder={question.placeholder}
          value={answer?.kind === 'text' ? answer.text : ''}
          error={Boolean(error)}
          helperText={error ?? ''}
          onChange={(event) => form.setText(question.id, event.target.value)}
          sx={{mt: 1.25, bgcolor: 'background.paper'}}
        />
      </Box>
    );
  }

  const tiles = question.type === 'boolean';

  const isOn = (option: FormOption): boolean => {
    if (answer?.kind === 'single') return answer.value === option.value;
    if (answer?.kind === 'multi') return option.value in answer.selections;
    return false;
  };

  const pick = (option: FormOption) => {
    if (question.type === 'multi-select') {
      form.toggleMulti(question.id, option.value, EXCLUSIVE_VALUE);
    } else {
      form.setSingle(question.id, option.value);
    }
  };

  // The note an option with an additionalTextField currently holds. Single
  // answers keep one note; multi answers key the note by the option's value.
  const noteFor = (option: FormOption): string => {
    if (answer?.kind === 'single') return answer.note;
    if (answer?.kind === 'multi') return answer.selections[option.value] ?? '';
    return '';
  };

  const setNote = (option: FormOption, note: string) => {
    if (question.type === 'multi-select') {
      form.setMultiNote(question.id, option.value, note);
    } else {
      form.setSingleNote(question.id, note);
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 240px) minmax(0, 1fr)'},
        gap: '12px 24px',
        py: 2.25,
        borderTop: divider ? '1px solid' : 0,
        borderColor: 'divider',
      }}>
      <Box>
        <Typography sx={{fontSize: 14.5, fontWeight: 600, lineHeight: 1.4}}>
          {question.label}
          {question.required && (
            <Box
              aria-hidden
              component="span"
              sx={{color: 'warning.main', ml: 0.5}}>
              *
            </Box>
          )}
        </Typography>
        {question.description && (
          <Typography
            sx={{
              mt: 0.75,
              fontSize: 13,
              lineHeight: 1.5,
              color: 'text.secondary',
            }}>
            {question.description}
          </Typography>
        )}
        {error && (
          <Typography sx={{mt: 0.75, fontSize: 12.5, color: 'error.main'}}>
            {error}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: {xs: 'flex-start', md: 'flex-end'},
          alignContent: 'flex-start',
          gap: tiles ? 1.25 : 1,
        }}>
        {'fields' in question &&
          question.fields.map((option) => (
            <OptionButton
              key={option.id}
              option={option}
              on={isOn(option)}
              tile={tiles}
              onPick={() => pick(option)}
            />
          ))}
        {/* A selected option can request a follow-up explanation; reveal its
            input and wire it to the matching note setter. */}
        {'fields' in question &&
          question.fields
            .filter((option) => isOn(option) && option.additionalTextField)
            .map((option) => {
              const textField = option.additionalTextField!;
              const note = noteFor(option);
              const noteError =
                form.submitted && textField.required && note.trim() === '';
              return (
                <TextField
                  key={`${option.id}-note`}
                  fullWidth
                  aria-label={`${option.label} — additional detail`}
                  placeholder={textField.placeholder}
                  value={note}
                  error={noteError}
                  inputProps={
                    textField.maxLength
                      ? {maxLength: textField.maxLength}
                      : undefined
                  }
                  onChange={(event) => setNote(option, event.target.value)}
                  sx={{width: '100%', bgcolor: 'background.paper'}}
                />
              );
            })}
        {question.type === 'rating' &&
          Array.from(
            {length: question.max - question.min + 1},
            (_, index) => question.min + index,
          ).map((value) => (
            <OptionButton
              key={value}
              option={{
                id: String(value),
                label: String(value),
                value: String(value),
              }}
              on={answer?.kind === 'rating' && answer.value === value}
              tile={false}
              onPick={() => form.setRating(question.id, value)}
            />
          ))}
      </Box>
    </Box>
  );
};
